import type { MaterialsNodeData, ProposalNodeSet, TimberListItem, TradeBriefingTradeType } from '../types';
import type {
  TradeBriefingDeckingItem,
  TradeBriefingInstallContext,
  TradeBriefingSpeciesSpec,
  TradeBriefingStructureScope,
  TradeBriefingTimberLine,
  TradeBriefingTravelContext,
} from '../types';
import { prettyDryness } from './clientTimberSummary';
import { inferTimberDefaultsFromList } from './proposalTimberDefaults';
import { expandTimberDryness } from './timberDisplayLabels';

/** Loose node-set shape for sanitization (proposal builder or snapshots). */
export type TradeBriefingNodeSetSource = Pick<
  ProposalNodeSet,
  | 'id'
  | 'name'
  | 'quantity'
  | 'enabled'
  | 'images'
  | 'notes'
  | 'design'
  | 'production'
  | 'freight'
  | 'overhead'
  | 'install'
  | 'travel'
  | 'materials'
  | 'billablePrices'
>;

const sortCi = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

function normalizeDrynessLabel(raw?: string | null): string {
  const expanded = expandTimberDryness(raw);
  if (expanded === 'Kiln Dried') return 'Kiln-dried';
  return expanded;
}

function drynessLabelFromItem(item: TimberListItem): string {
  const pretty = prettyDryness(item.moistureContent);
  if (pretty === '—') return '';
  return pretty;
}

function dominantGrade(list: TimberListItem[]): string {
  const counts = new Map<string, number>();
  for (const item of list) {
    const grade = item.grade?.trim();
    if (!grade) continue;
    const weight = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
    counts.set(grade, (counts.get(grade) ?? 0) + weight);
  }
  if (!counts.size) return '';
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || sortCi(a[0], b[0]))[0]![0];
}

function drynessLabelsFromList(list: TimberListItem[]): string[] {
  const labels = new Set<string>();
  for (const item of list) {
    const dry = drynessLabelFromItem(item);
    if (dry) labels.add(dry);
  }
  const inferred = inferTimberDefaultsFromList(list);
  const inferredDry = normalizeDrynessLabel(inferred?.moistureContent);
  if (inferredDry) labels.add(inferredDry);

  if (!labels.size) {
    let green = 0;
    let kiln = 0;
    for (const item of list) {
      if (item.moistureContent === 'green') green += 1;
      else if (item.moistureContent === 'kiln_dried') kiln += 1;
    }
    if (green > kiln) labels.add('Green');
    else if (kiln > green) labels.add('Kiln-dried');
  }

  return Array.from(labels).sort(sortCi);
}

function gradeLabelsFromList(list: TimberListItem[]): string[] {
  const labels = new Set<string>();
  for (const item of list) {
    if (item.grade?.trim()) labels.add(item.grade.trim());
  }
  const inferred = inferTimberDefaultsFromList(list);
  if (inferred?.grade?.trim()) labels.add(inferred.grade.trim());
  const dominant = dominantGrade(list);
  if (dominant) labels.add(dominant);
  return Array.from(labels).sort(sortCi);
}

function speciesSpecsFromMaterials(materials: MaterialsNodeData | undefined): TradeBriefingSpeciesSpec[] {
  if (!materials) return [];

  const specs: TradeBriefingSpeciesSpec[] = [];
  const seen = new Set<string>();

  const addFromList = (list: TimberListItem[], forcedSpecies?: string) => {
    if (!list.length) return;
    const bySpecies = new Map<string, TimberListItem[]>();

    for (const item of list) {
      const species = (forcedSpecies || item.species || '').trim();
      if (!species) continue;
      const key = species.toLowerCase();
      if (!bySpecies.has(key)) bySpecies.set(key, []);
      bySpecies.get(key)!.push(item);
    }

    Array.from(bySpecies.entries()).forEach(([key, items]) => {
      if (seen.has(key)) return;
      seen.add(key);
      const species = (forcedSpecies || items[0]?.species || '').trim();
      if (!species) return;
      const grades = gradeLabelsFromList(items);
      const dryness = drynessLabelsFromList(items);
      specs.push({
        species,
        grade: grades.length ? grades.join(', ') : undefined,
        dryness: dryness.length ? dryness.join(', ') : undefined,
      });
    });
  };

  addFromList(materials.timberList ?? []);
  if (materials.quoteMultipleSpecies) {
    for (const alt of materials.alternateSpecies ?? []) {
      addFromList(alt.timberList ?? [], alt.speciesName?.trim());
    }
  }

  for (const alt of materials.alternateSpecies ?? []) {
    const species = alt.speciesName?.trim();
    if (!species) continue;
    const key = species.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const grades = gradeLabelsFromList(alt.timberList ?? []);
    const dryness = drynessLabelsFromList(alt.timberList ?? []);
    specs.push({
      species,
      grade: grades.length ? grades.join(', ') : undefined,
      dryness: dryness.length ? dryness.join(', ') : undefined,
    });
  }

  return specs.sort((a, b) => sortCi(a.species, b.species));
}

function collectTradeBriefingTimberMeta(materials: MaterialsNodeData | undefined): {
  gradeLabels: string[];
  drynessLabels: string[];
  speciesSpecs: TradeBriefingSpeciesSpec[];
} {
  const speciesSpecs = speciesSpecsFromMaterials(materials);
  const gradeLabels = new Set<string>();
  const drynessLabels = new Set<string>();

  for (const spec of speciesSpecs) {
    if (spec.grade) {
      for (const grade of spec.grade.split(',').map(g => g.trim()).filter(Boolean)) {
        gradeLabels.add(grade);
      }
    }
    if (spec.dryness) {
      for (const dry of spec.dryness.split(',').map(d => d.trim()).filter(Boolean)) {
        drynessLabels.add(dry);
      }
    }
  }

  return {
    gradeLabels: Array.from(gradeLabels).sort(sortCi),
    drynessLabels: Array.from(drynessLabels).sort(sortCi),
    speciesSpecs,
  };
}

function distinctSpecies(nodeSet: TradeBriefingNodeSetSource): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of nodeSet.materials?.timberList ?? []) {
    const species = item.species?.trim();
    if (!species) continue;
    const key = species.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(species);
  }
  for (const alt of nodeSet.materials?.alternateSpecies ?? []) {
    const label = alt.speciesName?.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

function sanitizeDeckingItems(nodeSet: TradeBriefingNodeSetSource): TradeBriefingDeckingItem[] | undefined {
  const items = nodeSet.materials?.deckingItems;
  if (!items?.length) return undefined;
  return items.map(item => ({
    id: item.id,
    productName: item.productName,
    widthInches: item.widthInches,
    sqFt: item.sqFt,
  }));
}

function sanitizeTimberLines(nodeSet: TradeBriefingNodeSetSource): TradeBriefingTimberLine[] | undefined {
  const list = nodeSet.materials?.timberList;
  if (!list?.length) return undefined;
  return list.map(item => ({
    name: item.name,
    species: item.species,
    grade: item.grade,
    dimensions: item.dimensions,
    quantity: item.quantity,
    boardFeet: item.boardFeet,
    dryness: drynessLabelFromItem(item) || undefined,
  }));
}

function sanitizeInstallContext(nodeSet: TradeBriefingNodeSetSource): TradeBriefingInstallContext | undefined {
  const install = nodeSet.install;
  if (!install) return undefined;
  const ctx: TradeBriefingInstallContext = {};
  if (typeof install.days === 'number') ctx.days = install.days;
  if (install.craneRequired) {
    ctx.craneRequired = true;
    if (typeof install.craneDaysNeeded === 'number') ctx.craneDaysNeeded = install.craneDaysNeeded;
  }
  if (install.telehandlerRequired) {
    ctx.telehandlerRequired = true;
    if (typeof install.telehandlerDaysNeeded === 'number') ctx.telehandlerDaysNeeded = install.telehandlerDaysNeeded;
  }
  if (install.scissorLiftRequired) {
    ctx.scissorLiftRequired = true;
    if (typeof install.scissorLiftDaysNeeded === 'number') ctx.scissorLiftDaysNeeded = install.scissorLiftDaysNeeded;
  }
  if (install.basketLiftRequired) {
    ctx.basketLiftRequired = true;
  }
  if (Object.keys(ctx).length === 0) return undefined;
  return ctx;
}

function sanitizeTravelContext(nodeSet: TradeBriefingNodeSetSource): TradeBriefingTravelContext | undefined {
  const travel = nodeSet.travel;
  if (!travel) return undefined;
  const ctx: TradeBriefingTravelContext = {};
  if (typeof travel.travelDays === 'number') ctx.travelDays = travel.travelDays;
  if (typeof travel.hotelNights === 'number') ctx.hotelNights = travel.hotelNights;
  if (typeof travel.crewSize === 'number') ctx.crewSize = travel.crewSize;
  if (Object.keys(ctx).length === 0) return undefined;
  return ctx;
}

/**
 * Strip all commercial fields from a proposal node set and shape a structure
 * summary suitable for subcontractor trade briefings.
 */
export function sanitizeNodeSetForTradeBriefing(nodeSet: TradeBriefingNodeSetSource): TradeBriefingStructureScope {
  const speciesLabels = distinctSpecies(nodeSet);
  const timberMeta = collectTradeBriefingTimberMeta(nodeSet.materials);
  const scope: TradeBriefingStructureScope = {
    id: nodeSet.id,
    name: nodeSet.name,
  };
  if (typeof nodeSet.quantity === 'number') scope.quantity = nodeSet.quantity;
  if (nodeSet.enabled === false) scope.enabled = false;
  if (nodeSet.images?.length) scope.images = nodeSet.images.map(img => ({ url: img.url, name: img.name }));
  if (nodeSet.notes?.trim()) scope.notes = nodeSet.notes.trim();
  if (speciesLabels.length) scope.speciesLabels = speciesLabels;
  if (timberMeta.gradeLabels.length) scope.gradeLabels = timberMeta.gradeLabels;
  if (timberMeta.drynessLabels.length) scope.drynessLabels = timberMeta.drynessLabels;
  if (timberMeta.speciesSpecs.length) scope.speciesSpecs = timberMeta.speciesSpecs;
  const deckingItems = sanitizeDeckingItems(nodeSet);
  if (deckingItems?.length) scope.deckingItems = deckingItems;
  const timberLines = sanitizeTimberLines(nodeSet);
  if (timberLines?.length) scope.timberLines = timberLines;
  const installContext = sanitizeInstallContext(nodeSet);
  if (installContext) scope.installContext = installContext;
  const travelContext = sanitizeTravelContext(nodeSet);
  if (travelContext) scope.travelContext = travelContext;
  return scope;
}

/** Sanitize all enabled node sets (or all when none explicitly disabled). */
export function sanitizeNodeSetsForTradeBriefing(
  nodeSets: TradeBriefingNodeSetSource[] | undefined | null,
): TradeBriefingStructureScope[] {
  if (!nodeSets?.length) return [];
  return nodeSets
    .filter(ns => ns.enabled !== false)
    .map(sanitizeNodeSetForTradeBriefing);
}

/** New-link default: timber sizes/lengths on whenever structure scope is shown. */
export function defaultShowTimberList(_trade?: TradeBriefingTradeType | string): boolean {
  return true;
}

/** Drop timber lines only when the staff toggle is explicitly off. Missing flag stays visible. */
export function applyTimberListVisibility(
  structures: TradeBriefingStructureScope[],
  showTimberList: boolean,
): TradeBriefingStructureScope[] {
  if (showTimberList) return structures;
  return structures.map(({ timberLines: _omit, ...rest }) => rest);
}

/** Public cards never include crew-day / lift install details. */
export function omitInstallContextFromStructures(
  structures: TradeBriefingStructureScope[],
): TradeBriefingStructureScope[] {
  return structures.map(({ installContext: _omit, ...rest }) => rest);
}

export type TradeBriefingTimberDisplayRow = {
  structureName: string;
  structureQuantity?: number;
  name: string;
  size: string;
  length: string;
  quantity: number;
  species: string;
  grade: string;
  dryness: string;
};

/** Flatten structure timber lines into display / PDF rows. */
export function buildTradeBriefingTimberDisplayRows(
  structures: TradeBriefingStructureScope[],
): TradeBriefingTimberDisplayRow[] {
  const rows: TradeBriefingTimberDisplayRow[] = [];
  for (const structure of structures) {
    for (const line of structure.timberLines ?? []) {
      const dims = formatTradeBriefingTimberDims(line.dimensions);
      rows.push({
        structureName: structure.name,
        structureQuantity: structure.quantity,
        name: line.name?.trim() || 'Timber',
        size: dims.size || line.dimensions || '—',
        length: dims.length || '—',
        quantity: line.quantity,
        species: line.species?.trim() || '',
        grade: line.grade?.trim() || '',
        dryness: resolveTradeBriefingLineDryness(structure, line),
      });
    }
  }
  return rows;
}

/** Line board feet is per piece; structure quantity copies the listed set. */
export function sumTradeBriefingBoardFeet(structures: TradeBriefingStructureScope[]): number {
  return structures.reduce((sum, structure) => {
    const copies = typeof structure.quantity === 'number' && structure.quantity > 1
      ? structure.quantity
      : 1;
    const lineTotal = (structure.timberLines ?? []).reduce((lineSum, line) => {
      const boardFeet = Number(line.boardFeet) || 0;
      const quantity = Number(line.quantity) || 0;
      return lineSum + boardFeet * quantity;
    }, 0);
    return sum + lineTotal * copies;
  }, 0);
}

export function formatTradeBriefingBoardFeet(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} bd ft`;
}

/** Prefer the line dryness, then the matching species badge, then a single structure dryness. */
export function resolveTradeBriefingLineDryness(
  structure: Pick<TradeBriefingStructureScope, 'speciesSpecs' | 'drynessLabels'>,
  line: Pick<TradeBriefingTimberLine, 'dryness' | 'species'>,
): string {
  if (line.dryness?.trim()) return line.dryness.trim();
  const species = (line.species || '').trim().toLowerCase();
  const spec = structure.speciesSpecs?.find(
    item => (item.species || '').trim().toLowerCase() === species,
  );
  if (spec?.dryness?.trim()) return spec.dryness.trim();
  if (structure.drynessLabels?.length === 1) return structure.drynessLabels[0] || '';
  return '';
}

/** Split a proposal `HxWxL` string into size (`6x8`) and order length (`12'`). */
export function formatTradeBriefingTimberDims(dimensions: string | undefined): {
  size: string;
  length: string;
} {
  const raw = (dimensions || '').trim();
  const match = raw.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
  if (!match) return { size: raw, length: '' };
  const a = parseFloat(match[1]);
  const b = parseFloat(match[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return { size: raw, length: '' };
  const size = a <= b ? `${match[1]}x${match[2]}` : `${match[2]}x${match[1]}`;
  return { size, length: `${match[3]}'` };
}

function normalizeSpecLabel(value: string | undefined): string {
  return (value || '').trim().toLowerCase();
}

/** Show species/grade columns when lines mix values or differ from structure badges. */
export function timberListShowsSpeciesGrade(
  structure: Pick<TradeBriefingStructureScope, 'speciesSpecs' | 'speciesLabels' | 'gradeLabels'>,
  lines: TradeBriefingTimberLine[],
): { showSpecies: boolean; showGrade: boolean } {
  const badgeSpecies = new Set(
    (structure.speciesSpecs?.map(spec => spec.species) ?? structure.speciesLabels ?? [])
      .map(normalizeSpecLabel)
      .filter(Boolean),
  );
  const badgeGrades = new Set(
    [
      ...(structure.speciesSpecs?.map(spec => spec.grade) ?? []),
      ...(structure.gradeLabels ?? []),
    ]
      .map(normalizeSpecLabel)
      .filter(Boolean),
  );
  const lineSpecies = lines.map(line => normalizeSpecLabel(line.species)).filter(Boolean);
  const lineGrades = lines.map(line => normalizeSpecLabel(line.grade)).filter(Boolean);
  const uniqueSpecies = new Set(lineSpecies);
  const uniqueGrades = new Set(lineGrades);
  return {
    showSpecies: uniqueSpecies.size > 1 || lineSpecies.some(species => !badgeSpecies.has(species)),
    showGrade: uniqueGrades.size > 1 || lineGrades.some(grade => !badgeGrades.has(grade)),
  };
}

/** Returns true when a serialized object still carries commercial pricing keys. */
export function tradeBriefingPayloadHasPricing(value: unknown): boolean {
  const pricingKeys = new Set([
    'billablePrices',
    'timberTotal',
    'total',
    'unitCost',
    'pricePerLinFt',
    'teamLaborPerDay',
    'craneRatePerDay',
    'telehandlerRatePerDay',
    'scissorLiftRatePerDay',
    'hotelRatePerNight',
    'perDiemPerPersonPerDay',
    'fuelBudget',
    'costPerMileToAspers',
    'costPerMileToJobSite',
    'flatFeeToAspers',
    'flatFeeToJobSite',
    'pegsCost',
    'postBasesUnitCost',
    'postBasesTotal',
    'fastenerPackageCost',
    'deckingPricePerLinFt',
    'deckingTotal',
    'margin',
    'subtotal',
    'design',
    'timberKit',
    'install',
    'travel',
    'speciesOptions',
  ]);

  const stack: unknown[] = [value];
  while (stack.length) {
    const current = stack.pop();
    if (current == null || typeof current !== 'object') continue;
    if (Array.isArray(current)) {
      stack.push(...current);
      continue;
    }
    for (const [key, nested] of Object.entries(current as Record<string, unknown>)) {
      if (pricingKeys.has(key)) {
        const num = typeof nested === 'number' ? nested : NaN;
        if (typeof nested === 'object' && nested !== null) return true;
        if (typeof nested === 'number' && Number.isFinite(num) && num !== 0) return true;
        if (key === 'billablePrices' || key === 'speciesOptions') return true;
      }
      stack.push(nested);
    }
  }
  return false;
}
